import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import TourBlurBackdrop from '../../views/brand-store/components/TourBlurBackdrop';
import AnimatedPointer from './AnimatedPointer';
import { buildSteps } from './tourSteps';
import { isProfileSurveyOpen } from '../shared-components/DepartmentModal';
import { hasCatalogAccess } from '../../utils/accessControl';
import { addUserAttribute } from '../../api/users';

// Persist "seen" server-side so the tour shows ONCE per user — EXACTLY like the
// prod CatalogTour. We REUSE the SAME attribute, so users who already saw the old
// tour (have the flag) never see this one either; only genuinely new users (no flag)
// get it once. Do NOT use a fresh attribute here — that would re-show it to everyone.
const ATTRIBUTE_NAME = 'CATALOG_IN_YOUR_BRAND_ONBOARDING';
const hasAttribute = (attributes, name) =>
  Array.isArray(attributes) && attributes.some((attr) => attr.name === name);

const CARD_W = 360;
const MIN_DESKTOP = 1024;

// Module-level tour state. The OnboardingTour component REMOUNTS when the app swaps
// CatalogLayout <-> BrandStoreLayout on navigation (catalog product -> brand store),
// which would reset React state and kill the tour mid-flow. Mirroring active/stepIndex/
// finished here lets the fresh instance resume exactly where it left off. It resets on
// a full page reload (module re-evaluates), so reload still restarts the tour.
const tourMem = { active: false, stepIndex: 0, finished: false };

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

// First VISIBLE element matching the selector — desktop/mobile duplicates share
// selectors and the hidden one (display:none) must not win.
const queryVisible = (selector) => {
  if (!selector) return null;
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    if (el.getClientRects().length) return el;
  }
  return null;
};

// Wait for a visible match to appear in the DOM (or resolve null on timeout).
const waitForEl = async (selector, timeout = 5000) => {
  if (!selector) return null;
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const el = queryVisible(selector);
    if (el) return el;
    if (Date.now() - start > timeout) return null;
    // eslint-disable-next-line no-await-in-loop
    await wait(150);
  }
};

const computePos = (rect, placement, cardH) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = 16;
  if (!rect || placement === 'center') {
    return { top: Math.max(m, vh / 2 - cardH / 2), left: vw / 2 - CARD_W / 2 };
  }
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  let top;
  let left;
  switch (placement) {
    case 'top':
      top = rect.y - cardH - m;
      left = cx - CARD_W / 2;
      break;
    case 'left':
      top = cy - cardH / 2;
      left = rect.x - CARD_W - m;
      break;
    case 'right':
      top = cy - cardH / 2;
      left = rect.x + rect.width + m;
      break;
    case 'bottom':
    default:
      top = rect.y + rect.height + m;
      left = cx - CARD_W / 2;
      break;
  }
  return {
    top: clamp(top, m, vh - cardH - m),
    left: clamp(left, m, vw - CARD_W - m),
  };
};

const OnboardingTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // <=1024 = mobile layout (sidebar becomes the hamburger menu, mobile search, etc.)
  const isMobile = width <= MIN_DESKTOP;
  const steps = useMemo(() => buildSteps({ isMobile }), [isMobile]);

  // Init from module memory so a remount (layout swap) resumes the tour in place.
  const [active, setActive] = useState(tourMem.active);
  const [stepIndex, setStepIndex] = useState(tourMem.stepIndex);
  const [rect, setRect] = useState(null); // spotlight target rect
  const [cardRect, setCardRect] = useState(null); // element the popover is placed next to
  const [ready, setReady] = useState(false);
  const [paused, setPaused] = useState(false); // overlay hidden while a locked-feature modal is open
  const [cardH, setCardH] = useState(210);
  const [loadingPhase, setLoadingPhase] = useState(false); // step content still loading (e.g. templates generating)
  const cardRef = useRef(null);
  const enteredRef = useRef(null); // step id whose onEnter already ran
  const busyRef = useRef(false); // during onNext transitions
  const stepPathsRef = useRef({}); // stepIndex -> pathname where it was shown (for Back)
  // "done" lives in memory (not sessionStorage), so a full page reload restarts
  // the tour from the beginning — but finishing it within a session keeps it closed.
  const finishedRef = useRef(tourMem.finished);

  // Mirror active/stepIndex into module memory so the post-remount instance restores them.
  useEffect(() => { tourMem.active = active; }, [active]);
  useEffect(() => { tourMem.stepIndex = stepIndex; }, [stepIndex]);

  const step = steps[stepIndex];

  const finish = useCallback(() => {
    // Persist server-side that this user has seen the tour (closing with X counts
    // too), so it never auto-shows again — matches the old CatalogTour behaviour.
    addUserAttribute(ATTRIBUTE_NAME).catch(() => {});
    finishedRef.current = true;
    tourMem.finished = true;
    setActive(false);
    setReady(false);
    setRect(null);
    enteredRef.current = null;
    document.body.classList.remove('onb-tour-active');
    document.querySelector('.sidebar-nav')?.classList.remove('onb-expanded');
    window.dispatchEvent(new CustomEvent('tour-toggle-mobile-menu', { detail: { open: false } }));
  }, []);

  // Auto-start on catalog home. "finished" lives in memory (finishedRef / tourMem),
  // so closing with X (or finishing) hides it for the rest of the session, but a full
  // page reload restarts it from the beginning.
  useEffect(() => {
    if (active) return;
    const catalogHome = `${APP_PREFIX_PATH}/catalog`;
    const onCatalogHome =
      location.pathname === catalogHome || location.pathname === `${catalogHome}/`;
    if (!onCatalogHome) return;
    // Testing hatch: `?tour=1` force-starts the tour, bypassing the "finished" flag
    // AND the profile-survey gate — so it can be re-triggered without spinning up a
    // fresh account. No effect in prod unless the param is explicitly present.
    const forced = new URLSearchParams(location.search).get('tour') === '1';
    if (!forced) {
      if (finishedRef.current) return;
      // Only for users with catalog access, and only once per user (persisted via
      // ATTRIBUTE_NAME on finish) — same gating as the old prod CatalogTour.
      if (!hasCatalogAccess(user)) return;
      if (hasAttribute(user?.attributes, ATTRIBUTE_NAME)) return;
      // Wait for the blocking start-of-session modals before painting the tour
      // (z 10050) over them — otherwise it covers Terms/Department and traps the
      // user (it globally locks scroll + keyboard). Terms first, then the profile
      // survey — gated on the SAME cookie the modal uses (the old sessionStorage
      // 'department_modal_skipped' check was never written, so a skipped survey
      // blocked the tour forever).
      if (!user?.has_agreed_to_privacy_policy) return;
      if (isProfileSurveyOpen(user)) return;
    }
    const timer = setTimeout(() => setActive(true), 600);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, active, user]);

  useEffect(() => {
    if (active) document.body.classList.add('onb-tour-active');
    return () => document.body.classList.remove('onb-tour-active');
  }, [active]);

  // Resolve the current step: run onEnter once, then locate its target.
  useEffect(() => {
    if (!active || !step) return undefined;
    const matches = step.match(location.pathname);
    if (!matches) {
      setReady(false);
      setRect(null);
      // If the step lives on a fixed page, drive the navigation here (controller-
      // driven, so it doesn't depend on the previous step's onNext timing).
      // Compare only the path part so a goTo with a ?query doesn't loop.
      if (step.goTo && location.pathname !== step.goTo.split('?')[0]) {
        navigate(step.goTo);
      }
      return undefined;
    }

    let cancelled = false;
    setReady(false);

    (async () => {
      // Force the collapsed sidebar open for steps that highlight a nav item, so
      // labels show and the spotlight makes sense; collapse it again otherwise.
      const nav = document.querySelector('.sidebar-nav');
      if (nav) {
        if (!isMobile && step.expandSidebar) {
          nav.classList.add('onb-expanded');
          await wait(320); // let the expand transition settle before measuring
        } else {
          nav.classList.remove('onb-expanded');
        }
      }
      // Mobile: open the hamburger menu for nav/invite steps, close it otherwise.
      if (isMobile) {
        window.dispatchEvent(new CustomEvent('tour-toggle-mobile-menu', { detail: { open: !!step.mobileMenu } }));
        if (step.mobileMenu) {
          await wait(450); // drawer open animation
          // Scroll the target item near the top of the drawer's own scroll container
          // so it isn't hidden behind the tour card.
          const item = await waitForEl(step.selector, 2500);
          if (item) {
            let sp = item.parentElement;
            while (sp) {
              const s = getComputedStyle(sp);
              if (/(auto|scroll)/.test(s.overflowY + s.overflow) && sp.scrollHeight > sp.clientHeight + 4) break;
              sp = sp.parentElement;
            }
            if (sp) {
              sp.scrollTop += item.getBoundingClientRect().top - sp.getBoundingClientRect().top - 90;
              await wait(250);
            }
          }
        }
      }
      if (cancelled) return;
      if (enteredRef.current !== step.id && typeof step.onEnter === 'function') {
        enteredRef.current = step.id;
        // Fire-and-forget: onEnter does setup (scroll to the section, select a
        // template, navigate) but must NEVER hold the step from appearing. Waiting
        // on a guaranteed-but-slow request (e.g. template generation) froze the page
        // for seconds between steps. The step now shows as soon as its target
        // exists; the spotlight glues on via the recompute poll once content lands.
        Promise.resolve(step.onEnter({ navigate })).catch(() => {});
      } else {
        enteredRef.current = step.id;
      }
      if (cancelled) return;
      const el = await waitForEl(step.selector);
      if (cancelled) return;
      setRect(el ? el.getBoundingClientRect() : null);
      const cardEl = step.cardSelector ? queryVisible(step.cardSelector) : el;
      setCardRect(cardEl ? cardEl.getBoundingClientRect() : el ? el.getBoundingClientRect() : null);
      stepPathsRef.current[stepIndex] = location.pathname; // remember for Back
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [active, stepIndex, location.pathname, step, navigate]);

  // Keep the cutout + popover glued to the target on scroll / resize AND while the
  // layout shifts under it — e.g. the top-bar Invite avatars load asynchronously and
  // push the "show in my brand" toggle sideways after we first measured it. A cheap
  // poll re-measures and only updates state when the rect actually moved (no churn).
  useEffect(() => {
    if (!active || !ready || !step?.selector) return undefined;
    let lastR = null;
    let lastC = null;
    const same = (a, b) =>
      a && b && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
    const recompute = () => {
      const el = queryVisible(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (!same(r, lastR)) { lastR = r; setRect(r); }
      }
      const cardEl = step.cardSelector ? queryVisible(step.cardSelector) : el;
      if (cardEl) {
        const c = cardEl.getBoundingClientRect();
        if (!same(c, lastC)) { lastC = c; setCardRect(c); }
      }
    };
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    const iv = setInterval(recompute, 250); // track late/async layout shifts
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
      clearInterval(iv);
    };
  }, [active, ready, step]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [stepIndex, ready, rect]);

  // Some steps (e.g. templates on a brand-new account) render a loading/generating
  // state before their real content exists. While the step's `loadingSelector`
  // matches a visible element, show its `titleLoading`/`bodyLoading` copy; swap
  // back to the normal copy the instant that element disappears.
  useEffect(() => {
    if (!active || !ready || !step?.loadingSelector) {
      setLoadingPhase(false);
      return undefined;
    }
    const check = () => {
      const present = !!queryVisible(step.loadingSelector);
      setLoadingPhase((prev) => (prev !== present ? present : prev));
    };
    check();
    const iv = setInterval(check, 300);
    return () => clearInterval(iv);
  }, [active, ready, step]);

  // Emphasize the specific nav item within the opened sidebar (spotlight covers
  // the whole sidebar; this ring points out the one the step is about).
  useEffect(() => {
    document.querySelectorAll('.onb-nav-highlight').forEach((el) => el.classList.remove('onb-nav-highlight'));
    if (active && ready && step?.expandSidebar && step?.pointerSelector) {
      queryVisible(step.pointerSelector)?.classList.add('onb-nav-highlight');
    }
    return () => {
      document.querySelectorAll('.onb-nav-highlight').forEach((el) => el.classList.remove('onb-nav-highlight'));
    };
  }, [active, ready, step]);

  // Mobile hamburger steps: dim the drawer and pop the highlighted item using the
  // in-drawer overlay CSS (`.tour-hamburger-open .mobile-menu-wrapper` +
  // `[data-tour-highlight]`), which lives INSIDE the drawer's own stacking context —
  // the tour's blur backdrop can't reach in there (separate lower z-context).
  useEffect(() => {
    const clear = () => {
      document.body.classList.remove('tour-hamburger-open');
      document.querySelectorAll('[data-tour-highlight="true"]').forEach((el) => el.removeAttribute('data-tour-highlight'));
    };
    clear();
    if (active && ready && isMobile && step?.mobileMenu) {
      document.body.classList.add('tour-hamburger-open');
      const target = queryVisible(step.selector);
      // Highlight the nearest menu-row/link ancestor so the white band wraps the row.
      const row = target?.closest('.mobile-menu-link, .menu-link-wrapper, .mobile-menu-bottom') || target;
      row?.setAttribute('data-tour-highlight', 'true');
    }
    return clear;
  }, [active, ready, isMobile, step]);

  const goNext = useCallback(async () => {
    if (busyRef.current || !step) return;
    if (typeof step.onNext === 'function') {
      busyRef.current = true;
      setReady(false);
      await step.onNext({ navigate });
      busyRef.current = false;
    }
    if (stepIndex >= steps.length - 1) {
      finish();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [step, stepIndex, steps.length, navigate, finish]);

  const goPrev = useCallback(() => {
    if (busyRef.current || stepIndex === 0) return;
    const prev = stepIndex - 1;
    enteredRef.current = null; // force the previous step's onEnter to re-run
    const prevPath = stepPathsRef.current[prev];
    if (prevPath && prevPath !== location.pathname) {
      navigate(prevPath); // go back to the page where that step lived
    }
    setReady(false);
    setStepIndex(prev);
  }, [stepIndex, location.pathname, navigate]);

  // While the guided tour runs (and is not paused for a modal), block all keyboard
  // input so the user can't type into the search box, hit Escape to close it, etc.
  // and derail the scripted flow. The tour advances only via its own buttons.
  useEffect(() => {
    // Lock input/scroll for the WHOLE time the tour is active (steps AND the
    // transitions between them) so the user can't click around, change page or
    // cancel actions mid-flow. Released only while paused (a locked-feature modal
    // is open). Steps always resolve to `ready` within their onEnter/waitForEl
    // timeout (~6-11s), so this can never trap the user permanently.
    if (!active || paused) return undefined;
    const blockKey = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    // Block manual scrolling — the tour moves the user to the right zone itself.
    // scrollIntoView() is programmatic and is NOT affected by these preventDefaults.
    // EXCEPTION: on an `interactiveSelectors` step (e.g. the templates carousel),
    // allow wheel/swipe INSIDE that region so the user can browse it — desktop
    // wheel-scroll and, importantly, mobile swipe (there are no other scroll blocks
    // to work around on touch). Page scroll everywhere else stays blocked.
    const interactive = Array.isArray(step?.interactiveSelectors) ? step.interactiveSelectors : null;
    const blockScroll = (e) => {
      if (
        interactive &&
        e.target instanceof Element &&
        interactive.some((sel) => e.target.closest(sel))
      ) {
        return; // let the carousel scroll/swipe
      }
      e.preventDefault();
    };
    document.addEventListener('keydown', blockKey, true);
    document.addEventListener('wheel', blockScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', blockScroll, { passive: false, capture: true });
    return () => {
      document.removeEventListener('keydown', blockKey, true);
      document.removeEventListener('wheel', blockScroll, { capture: true });
      document.removeEventListener('touchmove', blockScroll, { capture: true });
    };
  }, [active, paused, ready, step]);

  // "Book a meeting" opens the same locked-feature modal the sidebar item opens.
  // The tour overlay (z 10050) would cover that modal, so we pause (hide the overlay)
  // while the modal is open and resume automatically once it closes.
  const openLockedModal = useCallback((kind) => {
    const sel = kind === 'wardrobe' ? '[data-tour-id="wardrobe-menu"]' : '[data-tour-id="agency"]';
    const el = document.querySelector(sel);
    if (!el) return;
    setPaused(true);
    el.click();
    const started = Date.now();
    let sawOpen = false;
    const iv = setInterval(() => {
      const wrap = document.querySelector('.ant-modal-wrap');
      const open = !!wrap && wrap.offsetParent !== null && getComputedStyle(wrap).display !== 'none';
      if (open) sawOpen = true;
      const elapsed = Date.now() - started;
      if ((sawOpen && !open) || (!sawOpen && elapsed > 2500)) {
        clearInterval(iv);
        setPaused(false);
      }
    }, 400);
  }, []);

  const selector = step?.selector && step.match(location.pathname) ? step.selector : null;
  const pos = computePos(ready ? cardRect || rect : null, step?.placement, cardH);

  // Optional precise pointer target: point the hand at a specific element
  // (e.g. the selected price cell) rather than a fraction of the spotlight area.
  const pointerEl = ready && step?.pointerSelector ? queryVisible(step.pointerSelector) : null;
  const pointerRect = pointerEl ? pointerEl.getBoundingClientRect() : rect;
  const showOverlay = active && step;

  // Mount the overlay whenever the tour is active (not just when the step is
  // `ready`): the interaction shield below is NOT ready-gated, so it keeps blocking
  // clicks/navigation during the transition between steps too. The backdrop, hand
  // pointer and card stay ready-gated (they only appear once the step is resolved).
  if (!showOverlay || paused) return null;

  const isLast = stepIndex === steps.length - 1;
  const mobileMenuStep = isMobile && !!step.mobileMenu;

  // On steps that allow poking specific elements (e.g. the templates carousel +
  // its arrows), cut a hole in the click-shield over the union of those elements'
  // rects so those clicks reach the page; everything else stays blocked.
  let holeRect = null;
  if (ready && Array.isArray(step.interactiveSelectors)) {
    const rects = [];
    step.interactiveSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el.getClientRects().length) rects.push(el.getBoundingClientRect());
      });
    });
    if (rects.length) {
      const pad = 6;
      holeRect = {
        left: Math.max(0, Math.min(...rects.map((r) => r.left)) - pad),
        top: Math.max(0, Math.min(...rects.map((r) => r.top)) - pad),
        right: Math.min(window.innerWidth, Math.max(...rects.map((r) => r.right)) + pad),
        bottom: Math.min(window.innerHeight, Math.max(...rects.map((r) => r.bottom)) + pad),
      };
    }
  }

  const shieldBand = (key, style) => (
    <div
      key={key}
      className="onb-shield"
      style={{ position: 'fixed', zIndex: 2, pointerEvents: 'auto', background: 'transparent', ...style }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.preventDefault()}
    />
  );

  // The interaction shield: absorbs every click so the user can't press the search
  // X, click around, or break the scripted flow. Full-screen normally; on an
  // `interactiveSelectors` step it becomes four bands around `holeRect`, leaving
  // that region click-through. The tour card sits above it (z 3) and stays clickable.
  const shield = holeRect ? (
    <>
      {shieldBand('top', { left: 0, top: 0, right: 0, height: holeRect.top })}
      {shieldBand('bottom', { left: 0, top: holeRect.bottom, right: 0, bottom: 0 })}
      {shieldBand('left', {
        left: 0,
        top: holeRect.top,
        width: holeRect.left,
        height: holeRect.bottom - holeRect.top,
      })}
      {shieldBand('right', {
        left: holeRect.right,
        top: holeRect.top,
        right: 0,
        height: holeRect.bottom - holeRect.top,
      })}
    </>
  ) : (
    shieldBand('full', { inset: 0, cursor: ready ? 'default' : 'progress' })
  );

  return (
    <>
      {/* Mobile hamburger steps: the drawer is raised to z 1002 (.tour-hamburger-open)
          and dims itself internally. This separate page-dim sits at z 1001 — ABOVE the
          page but BELOW the drawer — so the products page behind the drawer is blurred
          too (matches the desktop spotlight feel). */}
      {mobileMenuStep &&
        ReactDOM.createPortal(<div className="onb-page-dim" />, document.body)}
      {ReactDOM.createPortal(
    <div className="onb-tour-root">
      <TourBlurBackdrop
        open={active && ready && !step.noBackdrop}
        targetSelector={ready ? selector : null}
      />

      {shield}

      {ready && pointerRect && step.placement !== 'center' && (
        <AnimatedPointer rect={pointerRect} at={step.pointerAt} />
      )}

      {ready && (
        <div
          ref={cardRef}
          className={`onb-card onb-card-${step.placement || 'bottom'}`}
          style={{ top: pos.top, left: pos.left, width: CARD_W }}
        >
          <button type="button" className="onb-card-close" onClick={finish} aria-label="Close">
            ✕
          </button>
          <div className="onb-card-step">
            {stepIndex + 1} / {steps.length}
          </div>
          <h3 className="onb-card-title">
            {loadingPhase && step.titleLoading ? step.titleLoading : step.title}
          </h3>
          <p className="onb-card-body">
            {loadingPhase && step.bodyLoading ? step.bodyLoading : step.body}
          </p>

          {step.cta && (
            step.cta.action ? (
              <button
                type="button"
                className="onb-card-cta"
                onClick={() => openLockedModal(step.cta.action)}
              >
                {step.cta.label}
              </button>
            ) : (
              <a
                className="onb-card-cta"
                href={step.cta.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {step.cta.label}
              </a>
            )
          )}

          <div className="onb-card-actions">
            {stepIndex > 0 && (
              <button type="button" className="onb-btn-ghost" onClick={goPrev}>
                Back
              </button>
            )}
            <button type="button" className="onb-btn-primary" onClick={goNext}>
              {step.nextLabel || (isLast ? 'Finish' : 'Next')}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  )}
    </>
  );
};

export default OnboardingTour;
